import ContainerPage from "../../../components/ContainerPage/ContainerPage"
import Breadcrumbs from "../../../components/BreadCrumbs/Breadcrumbs"
import React, { useEffect, useRef, useState } from "react"
import dayjs from "dayjs"
import { Stack } from "@mui/material"
import { ProgramPlan } from "./ProgramPlan"
import { CreditSelect } from "./CreditSelect/CreditSelect"
import { PaymentSchedule } from "./PaymentSchedule"
import { Review } from "./Review/Review"
import { BudgetCalculator } from "./BudgetCalculator"
import { useDispatch, useSelector } from "react-redux"
import { getLeadDetails, sendLeadDetails } from "../../../features/contacts/contacts"
import { selectFeatureFlag } from "../../../features/featureFlags/featureFlags"
import { CREDIT_SELECT_MANUAL_POPUP_ON_REQUEST, LEAD_DETAILS_SELF_HEAL, FLAT_FEE_TYPED_AMOUNT, PROGRAM_FEE_FLOOR_15, PARTNER_LEAD_SOURCE, CONTACT_EMAIL_OPTIONAL, OFFICE_LEAD_SOURCES, CONTACT_FINANCING_PENDING, WIZARD_DEBT_SUMMARY, FDR_MIN_ONE_PERCENT, ESIGN_LSA_PAYMENT_UPFRONT } from "../../../config/featureFlagKeys"
import { showSnackBar } from "../../../components/Snackbar/Snackbar"
import { useLocation, useNavigate } from "react-router-dom"
import { useMyContextLoading } from "../../../components/LoadingContextProvider/LoadingContextProvider"
import { BasicInfo } from "./BasicInfo"
import { getBankPayment, getCreditCard, getAffiliateBilling } from "../../../features/payment/paymentSlice"
import { resolveBillingMethod } from "./billingMethods"
import { isSignedMainDoc } from "../../../features/documents/documents"
import { LEAD_SOURCE_MAX_LENGTH, LEGACY_LEAD_SOURCE_MAX_LENGTH, disableTabsInit, getPostData, getTabs, paymentScheduleInit, programPlanInit, userInfoInit } from "./consts"
import PageTitle from "../../../components/PageTitle/PageTitle"
import { getNextWorkingDay } from "../../../utils/date"
import NotesArea from "../../../components/NotesArea/NotesArea"
import { getContactLinksByUserId } from "../../../features/contactLinks/contactLinks"
import { getContactGroupCustomSettings } from "../../../features/contactGroups/contactGroups"
import { StorageGetItem, USER } from "../../../lib/storage"
import { isCreditSelectionPersisted, sumSelectedBalances, toSavedCreditProfileIds } from "./helper"
import { needsOfficeConfig } from "./enrolmentGates"
import { emailOptionalFor } from "./emailOptional"
import { officeLeadSourcesFor } from "./officeLeadSources"
import FinancingMarkCheckbox from "../../Contact/Financing/FinancingMarkCheckbox"
import useLsaChargeName from "../../../hooks/useLsaChargeName"

export const AddContact = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isCreate } = location.state || {}
  const dispatch = useDispatch()
  const currentURL = window.location.href
  const parts = currentURL.split('/')
  const userIdByUrl = parts[parts.length - 1]
  const { triggerLoading } = useMyContextLoading()

  const liveFlag = useSelector(selectFeatureFlag(CREDIT_SELECT_MANUAL_POPUP_ON_REQUEST))
  const popupOnRequestRef = useRef(false)
  if (liveFlag) popupOnRequestRef.current = true
  const popupOnRequest = popupOnRequestRef.current

  const leadDetailsSelfHeal = useSelector(selectFeatureFlag(LEAD_DETAILS_SELF_HEAL))
  // Flat fee typed by hand rather than picked from the dropdown (WS #1944843).
  const flatFeeTyped = useSelector(selectFeatureFlag(FLAT_FEE_TYPED_AMOUNT))
  // WS #2346323: the Program Fee % list from 15% (Solar firm only).
  const lowerFeeFloor = useSelector(selectFeatureFlag(PROGRAM_FEE_FLOOR_15))
  const onePercentFloor = useSelector(selectFeatureFlag(FDR_MIN_ONE_PERCENT))
  const partnerLeadSource = useSelector(selectFeatureFlag(PARTNER_LEAD_SOURCE))
  const contactEmailOptional = useSelector(selectFeatureFlag(CONTACT_EMAIL_OPTIONAL))
  const officeLeadSourcesOn = useSelector(selectFeatureFlag(OFFICE_LEAD_SOURCES))
  const financingPendingEnabled = useSelector(selectFeatureFlag(CONTACT_FINANCING_PENDING))
  const lsaUpfrontOn = useSelector(selectFeatureFlag(ESIGN_LSA_PAYMENT_UPFRONT))
  const debtSummary = useSelector(selectFeatureFlag(WIZARD_DEBT_SUMMARY))

  const [reqError, setReqError] = useState('')
  const [userInfo, setUserInfo] = useState(userInfoInit)
  const [programPlan, setProgramPlan] = useState(programPlanInit)
  const [paymentSchedule, setPaymentSchedule] = useState(paymentScheduleInit)
  const [calculatorItems, setCalculatorItems] = useState([])
  const [custUserId, setCustUserId] = useState('')
  const chargeName = useLsaChargeName(custUserId)
  const [ssnNumber, setSsnNumber] = useState("")
  const [hasSsnOnFile, setHasSsnOnFile] = useState(false)
  const [selectedTab, setSelectedTab] = useState("basic-info")
  const [disableTabs, setDisableTabs] = useState(disableTabsInit)
  const [selectedCreditLines, setSelectedCreditLines] = useState([])
  const [savedCreditProfileIds, setSavedCreditProfileIds] = useState([])
  const [emailFromBank, setEmailFromBank] = useState("")
  const [confirmEmail, setConfirmEmail] = useState("")
  const [isBillingAccount, setIsBillingAccount] = useState(false)
  const [isCreditCheck, setIsCreditCheck] = useState(popupOnRequest ? null : false)
  const [isSignedLSA, setIsSignedLSA] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState({})
  const [skipQCCall, setSkipQCCall] = useState(false)
  const [hasCoApplicant, setHasCoApplicant] = useState(false)
  const [coApplicantInfo, setCoApplicantInfo] = useState({
    firstName: '', lastName: '', email: '', primaryPhone: '',
    ssNumber: '', dateOfBirth: '', middleName: '', gender: '',
    address: '', aptNumber: '', city: '', state: '', zip: '',
  })
  const [coApplicantId, setCoApplicantId] = useState('')
  const [contactGroupConfig, setContactGroupConfig] = useState(null)
  // WS #2219705: the rep's active office settings on the create path (PACK may enrol a client with no email).
  const [createOfficeSettings, setCreateOfficeSettings] = useState(null)
  const emailOptional = emailOptionalFor({ flagOn: contactEmailOptional, createOfficeSettings, contactGroupConfig })
  // WS #2296193: the office's own Lead Source list (Modern Financial), or null for today's free text.
  const leadSourceOptions = officeLeadSourcesFor({ flagOn: officeLeadSourcesOn, createOfficeSettings, contactGroupConfig })
  const pendingTierReapplyRef = useRef(false)
  const manualTierEditsRef = useRef({ programFee: false, custodialFee: false, plan: false })
  const creditFirstSyncRef = useRef(true)
  const recalcRequestedRef = useRef(false)
  const programmaticCreditSyncRef = useRef(false)
  const [leadCreatorId, setLeadCreatorId] = useState(null)


  useEffect(() => {
    const handleReqError = () => {
      if (reqError.length > 0) {
        showSnackBar("error", reqError)
        setReqError('')
      }
    }

    handleReqError()
  }, [reqError])

  useEffect(() => {
    !isCreate && getData()
  }, [])

  // The office config decides whether the flat-fee control may be shown, and it is resolved from the
  // CONTACT — so on the create path there is nothing to ask about until BasicInfo has made one.
  // Without this the control never appeared for a client the rep had just created.
  useEffect(() => {
    if (!needsOfficeConfig({ isCreate, custUserId, contactGroupConfig })) return

    dispatch(getLeadDetails(custUserId))
      // Merged, not replaced: loadCreatorGroupConfig may already have set plan-defaults here from
      // the creator's own groups, and dropping those would change the fee and length a rep is
      // offered.
      .then(r => {
        const fetched = r?.payload?.data?.contactGroupConfig
        if (fetched) setContactGroupConfig(prev => ({ ...prev, ...fetched }))
      })
      .catch(() => {})
  }, [isCreate, custUserId, contactGroupConfig])

  useEffect(() => {
    setConfirmEmail(userInfo?.email || emailFromBank || "")
  }, [userInfo?.email])

  useEffect(() => {
    if (!isCreate) return
    const loadCreatorGroupConfig = async () => {
      const stored = await StorageGetItem(USER)
      setLeadCreatorId(stored?._id || null)
      const activeOfficeId = localStorage.getItem('activeOfficeId')
      const allGroups = stored?.contactGroups || []
      const groupIds = activeOfficeId ? [activeOfficeId] : (allGroups.length === 1 ? allGroups : [])
      if (groupIds.length === 0) return

      const settings = await Promise.all(
        groupIds.map(gid => dispatch(getContactGroupCustomSettings({ groupId: gid })).then(r => r?.payload?.data?.customSettings || null).catch(() => null))
      )
      if (settings.length === 1) setCreateOfficeSettings(settings[0])
      const anyDisabled = settings.some(cs => cs?.disableInitialFee === true)
      const flagged = settings.find(cs => cs?.programPlanDefaults?.enabled === true)

      const config = {}

      if (flagged?.programPlanDefaults) {
        const block = flagged.programPlanDefaults
        const tiers = Array.isArray(block.tiers) ? block.tiers : []
        config.programPlanDefaults = {
          enabled: true,
          tiers: tiers.length > 0
            ? tiers
            : [{
                maxAmount: null,
                programFee: block.programFee == null ? null : block.programFee,
                custodialFee: block.custodialFee == null ? null : block.custodialFee,
                programLength: block.programLength == null ? null : block.programLength,
              }],
        }
      }

      if (anyDisabled) {
        config.disableInitialFee = true
      }

      if (Object.keys(config).length > 0) {
        setContactGroupConfig(config)
      }
    }
    loadCreatorGroupConfig()
  }, [isCreate])

  useEffect(() => {
    setProgramPlan((prev) => ({
      ...prev,
      amount: sumSelectedBalances(selectedCreditLines)
    }))
    if (programmaticCreditSyncRef.current) {
      programmaticCreditSyncRef.current = false
      creditFirstSyncRef.current = false
      return
    }
    if (creditFirstSyncRef.current) {
      creditFirstSyncRef.current = false
    } else {
      pendingTierReapplyRef.current = true
      recalcRequestedRef.current = true
    }
  }, [selectedCreditLines])

  useEffect(() => {
    const persisted = isCreditSelectionPersisted({ selectedCreditLines, savedCreditProfileIds })
    setDisableTabs((prev) => ({
      ...prev,
      programPlan: !persisted,
      paymentSchedule: persisted ? prev.paymentSchedule : true,
      review: persisted ? prev.review : true,
    }))
  }, [savedCreditProfileIds, selectedCreditLines])

  const checkUserBankAccountAndSigningInfo = async ({ custUserId }) => {
    try {
      if (custUserId) {
        const billingAccounts = await dispatch(getBankPayment(custUserId))
        const creditCard = await dispatch(getCreditCard(custUserId))

        const account = billingAccounts?.payload?.data
        const card = creditCard?.payload?.data
        // Asked separately because /bankaccounts queries the BankAccount discriminator and cannot
        // see an affiliate method. Without this an affiliate-billed client reads as having no
        // payment method at all, which is what left the LSA unsendable and put the old "add your
        // bank account" link in front of them instead.
        const affiliate = await dispatch(getAffiliateBilling(custUserId))
        const affiliateAccount = affiliate?.payload?.data || null

        const { hasBilling, paymentMethod: method } = resolveBillingMethod({
          bankAccounts: account,
          cards: card,
          affiliateAccount,
        })
        setPaymentMethod(method || {})
        const isMainDocSignedData = await dispatch(isSignedMainDoc({ userId: custUserId }))
        const isMainDocSigned = isMainDocSignedData?.payload?.data

        setIsBillingAccount(hasBilling)
        setIsSignedLSA(isMainDocSigned)

        if (hasBilling) {
          const leadResp = await dispatch(getLeadDetails(custUserId))
          const leadDetails = leadResp?.payload?.data
          const hasProfiles = (leadDetails?.creditSelect?.selectedCreditProfiles?.length || 0) > 0
          const hasProgramPlan = !!(leadDetails?.programPlan?.programFee && leadDetails?.programPlan?.programLength)
          const hasEstimatedPayments = (leadDetails?.paymentSchedule?.estimatedPayments?.length || 0) > 0

          if (hasProfiles && hasProgramPlan && hasEstimatedPayments) {
            setDisableTabs({
              creditSelect: true,
              programPlan: true,
              paymentSchedule: true,
              review: false,
            })
          }
        }

        // Stops Review's 5-second poll. It has to count the affiliate method too, or the screen
        // polls forever for a client who already has everything they are ever going to have.
        return hasBilling && isMainDocSigned
      }
    } catch (error) {
      console.error('An unexpected error occurred:', error)
    }
    return false
  }

  const getData = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getLeadDetails(userIdByUrl))
      const leadDetails = response?.payload?.data

      setContactGroupConfig(leadDetails?.contactGroupConfig || null)
      setLeadCreatorId(leadDetails?.leadCreatorId || null)
      setEmailFromBank(leadDetails?.bankConfirmationEmail)
      programmaticCreditSyncRef.current = true
      setSelectedCreditLines(leadDetails?.creditSelect?.selectedCreditProfiles)
      const savedIds = toSavedCreditProfileIds(leadDetails?.creditSelect?.selectedCreditProfiles)
      setSavedCreditProfileIds(savedIds)
      const hasSavedDebts = savedIds.length > 0
      if (leadDetails?.creditSelect?.creditType === 'softCreditCheck') {
        setIsCreditCheck(true)
      }
      if (leadDetails?.programPlan) {
        const { programLength, programFee } = leadDetails?.programPlan
        if (hasSavedDebts && programFee && programLength) {
          setDisableTabs(prev => ({
            ...prev,
            paymentSchedule: false,
          }))
        }

        if (hasSavedDebts && leadDetails?.paymentSchedule?.estimatedPayments?.length > 0) {
          setDisableTabs(prev => ({
            ...prev,
            review: false,
          }))
        }
        setProgramPlan(prev => ({
          ...prev,
          plan: leadDetails?.programPlan?.programLength || 18,
          programFee: leadDetails?.programPlan?.programFee ?? 50,
          custodialFee: leadDetails?.programPlan?.custodialFee ?? 50,
          initialFee: leadDetails?.programPlan?.initialFee || '',
          // Without these a reload drops the rep's fee pick and the next save re-prices the client.
          feeType: leadDetails?.programPlan?.feeType === 'flat' ? 'flat' : 'percent',
          flatFee: leadDetails?.programPlan?.feeType === 'flat' ? (leadDetails?.programPlan?.flatFee ?? null) : null,
        }))
        const freq = leadDetails?.paymentSchedule?.frequency || ""
        const startDt = leadDetails?.paymentSchedule?.startDate
        const secondDt = leadDetails?.paymentSchedule?.secondStartDate
        setPaymentSchedule(prev => ({
          ...prev,
          frequency: freq,
          cycleStartDate: startDt ? dayjs.utc(startDt) : dayjs(),
          secondDate: secondDt
            ? dayjs.utc(secondDt)
            : (freq === 'semiMonthly' && startDt ? dayjs.utc(startDt).add(15, 'day') : null),
          tableData: leadDetails?.paymentSchedule?.estimatedPayments
        }))
      }

      if (hasSavedDebts) {
        setDisableTabs(prev => ({
          ...prev,
          programPlan: false,
        }))
      } else {
        setDisableTabs(prev => ({
          ...prev,
          programPlan: true,
          paymentSchedule: true,
          review: true
        }))
      }

      await checkUserBankAccountAndSigningInfo({ custUserId: userIdByUrl })

      const linksRes = await dispatch(getContactLinksByUserId(userIdByUrl))
      if (Array.isArray(linksRes?.payload) && linksRes.payload.length > 0) {
        const coLink = linksRes.payload.find(l => l.linkType === 'co-applicant')
        if (coLink) {
          const coApplicant = coLink.primaryContact?._id === userIdByUrl ? coLink.linkedContact : coLink.primaryContact
          if (coApplicant?._id) {
            setHasCoApplicant(true)
            setCoApplicantId(coApplicant._id)
          }
        }
      }
    } catch (error) {
      console.error(error)
      setReqError(error.message || 'Failed to get customer data')
    }
    triggerLoading(false)
  }

  const getNewLeadDetails = async ({ userId }) => {
    try {
      const postData = getPostData({ programPlan, paymentSchedule, calculatorItems })

      const response = await dispatch(sendLeadDetails({ userId, postData }))
      if (response.error) {
        const errorMessage = response?.payload?.data || 'An error occurred. Please try again.'
        setReqError(errorMessage)
      } else {
        showSnackBar("success", "Successfully")
      }
    } catch (error) {
      setReqError('An unexpected error occurred. Please try again.')
    }
  }

  const saveNewLeadDetails = async ({ userId }) => {
    try {
      const postData = getPostData({ programPlan, paymentSchedule, calculatorItems })

      const response = await dispatch(sendLeadDetails({ userId, postData }))
      if (response.error) {
        setReqError(response?.payload || 'An error occurred. Please try again.')
        return false
      }
      showSnackBar("success", "Successfully")
      return true
    } catch (error) {
      setReqError('An unexpected error occurred. Please try again.')
      return false
    }
  }

  const BreadcrumbsActive = ["Dashboard", "User"]
  const BreadcrumbsTitle = [isCreate ? "Add Contact" : "Edit Contact"]

  const renderTabContent = () => {
    switch (selectedTab) {
      case "basic-info":
        return <div>
          <BasicInfo
            userInfo={userInfo}
            setUserInfo={setUserInfo}
            userId={custUserId}
            setUserId={setCustUserId}
            setDisableTabs={setDisableTabs}
            setSelectedTab={setSelectedTab}
            setSsnNumber={setSsnNumber}
            setHasSsnOnFile={setHasSsnOnFile}
            getNewLeadDetails={leadDetailsSelfHeal ? saveNewLeadDetails : getNewLeadDetails}
            gatesOnLeadDetails={leadDetailsSelfHeal}
            leadSourceMaxLength={partnerLeadSource ? LEAD_SOURCE_MAX_LENGTH : LEGACY_LEAD_SOURCE_MAX_LENGTH}
            isCreate={isCreate}
            isBillingAccount={isBillingAccount}
            checkUserBankAccountAndSigningInfo={checkUserBankAccountAndSigningInfo}
            skipQCCall={skipQCCall}
            hasCoApplicant={hasCoApplicant}
            setHasCoApplicant={setHasCoApplicant}
            coApplicantInfo={coApplicantInfo}
            setCoApplicantInfo={setCoApplicantInfo}
            coApplicantId={coApplicantId}
            setCoApplicantId={setCoApplicantId}
            emailOptional={emailOptional}
            leadSourceOptions={leadSourceOptions}
          />
        </div>
      case "credit-select":
        return <div>
          <CreditSelect
            getUserById={custUserId}
            ssnNumber={ssnNumber}
            setSsnNumber={setSsnNumber}
            hasSsnOnFile={hasSsnOnFile}
            setDisableTabs={setDisableTabs}
            setSelectedTab={setSelectedTab}
            userId={custUserId}
            isCreditCheck={isCreditCheck}
            setIsCreditCheck={setIsCreditCheck}
            popupOnRequest={popupOnRequest}
            setSelectedCreditLines={setSelectedCreditLines}
            selectedCreditLines={selectedCreditLines}
            onCreditSelectSaved={setSavedCreditProfileIds}
            coApplicantId={coApplicantId}
            hasCoApplicant={hasCoApplicant}
            debtSummary={debtSummary}
          />
        </div>
      case "program-plan":
        return <div>
          {/* WS 2311820: "is this client financing the fee?" while setting up the program (Solar). */}
          {financingPendingEnabled && (
            <div className="bg-surface border border-divider rounded-2xl w-full max-w-[798px] px-12 pb-4 mb-4">
              <FinancingMarkCheckbox userId={custUserId} />
            </div>
          )}
          <ProgramPlan
            programPlan={programPlan}
            setProgramPlan={setProgramPlan}
            paymentSchedule={paymentSchedule}
            setPaymentSchedule={setPaymentSchedule}
            setDisableTabs={setDisableTabs}
            setSelectedTab={setSelectedTab}
            isCreate={isCreate}
            userId={custUserId}
            selectedCreditLines={selectedCreditLines}
            contactGroupConfig={contactGroupConfig}
            pendingTierReapplyRef={pendingTierReapplyRef}
            manualTierEditsRef={manualTierEditsRef}
            recalcRequestedRef={recalcRequestedRef}
            leadCreatorId={leadCreatorId}
            flatFeeTyped={flatFeeTyped}
            lowerFeeFloor={lowerFeeFloor}
            debtSummary={debtSummary}
            onePercentFloor={onePercentFloor}
          />
        </div>
      case "payment-schedule":
        return <div>
          <PaymentSchedule
            programPlan={programPlan}
            paymentSchedule={paymentSchedule}
            setPaymentSchedule={setPaymentSchedule}
            setSelectedTab={setSelectedTab}
            userId={custUserId}
            isCreate={isCreate}
            selectedCreditLines={selectedCreditLines}
            getNextWorkingDay={getNextWorkingDay}
            setDisableTabs={setDisableTabs}
            recalcRequestedRef={recalcRequestedRef}
          />
        </div>
      case "review":
        return <div>
          <Review
            paymentSchedule={paymentSchedule}
            programPlan={programPlan}
            userId={custUserId}
            basicInfoEmail={userInfo?.email}
            emailOptional={emailOptional}
            emailFromBank={emailFromBank}
            confirmEmail={confirmEmail}
            setConfirmEmail={setConfirmEmail}
            primaryPhone={userInfo.primaryPhone}
            userInfo={userInfo}
            isBillingAccount={isBillingAccount}
            checkUserBankAccountAndSigningInfo={checkUserBankAccountAndSigningInfo}
            isSignedMainDoc={isSignedLSA}
            paymentMethod={paymentMethod}
            selectedCreditLines={selectedCreditLines}
            savedCreditProfileIds={savedCreditProfileIds}
            skipQCCall={skipQCCall}
            setSkipQCCall={setSkipQCCall}
            coApplicantId={coApplicantId}
            coApplicantInfo={coApplicantInfo}
            contactGroupConfig={contactGroupConfig}
            debtSummary={debtSummary}
            chargeName={chargeName}
            stopOnSendError={lsaUpfrontOn}
          />
        </div>
      case "budget-calculator":
        return <div>
          <BudgetCalculator
            calculatorItems={calculatorItems}
            setCalculatorItems={setCalculatorItems}
            userId={custUserId}
          />
        </div>
    }
  }

  const tabs = getTabs({ disableTabs })

  return <ContainerPage>
    <div>
      <PageTitle title="Account" onBack={() => navigate(-1)} />
      <Breadcrumbs title={BreadcrumbsTitle} titleActive={BreadcrumbsActive} />
    </div>
    <Stack
      display="flex"
      direction="row"
      spacing={3}
      className="mt-6"
    >
      <div
        className="bg-surface border border-divider shadow-lg rounded-2xl w-[280px] shrink-0 max-h-[775px]">
        {tabs?.map((tab, index) => (
          <div key={tab.id} className={`${index === tabs.length - 1 ? "border-t border-divider" : ""}`}>
            <div
              className={`flex items-center w-[232px] py-3.5 pl-4 rounded-lg m-6
                ${tab.disabled ? "cursor-not-allowed opacity-50" : "hover:bg-primary-main/5 cursor-pointer"}
                ${selectedTab === tab.id && !tab.disabled ? "bg-primary-main/10" : ""}`}
              onClick={() => !tab.disabled && setSelectedTab(tab.id)}
            >
              {/* Colour comes from the row's text colour, so the icon and its label
                  always match. The active state used --color-blue-700 while the
                  label used brand indigo, so they disagreed. */}
              <div className={`flex items-center ${selectedTab === tab.id && !tab.disabled ? "text-primary-main" : "text-text-secondary"}`}>
                <tab.icon size={18} />
              </div>
              <div
                className={`font-normal text-[14px] leading-[1.75] pl-[16px] ${selectedTab === tab.id && !tab.disabled ? "text-primary-main" : "text-text-secondary"}`}
              >
                {tab?.label}
              </div>
            </div>
          </div>
        ))}
        <div className='w-full flex justify-center px-4'>
          <NotesArea userId={custUserId} />
        </div>
      </div>
      <div className="flex-1 min-w-0 max-w-[798px]">
        {renderTabContent()}
      </div>
    </Stack>
  </ContainerPage>
}
